import React, { useEffect, useState } from 'react'
import type { TFunction } from 'i18next'
import { DistributionSection, type DistributionData } from '../../../DistributionSection'
import { processMarketDistributionData } from '../../../DistributionSection/processMarketDistributionData'
import { usePurchaseMarket } from '../../PurchaseMarketContext'

interface DistributionProps {
  t: TFunction
}

export const Distribution: React.FC<DistributionProps> = ({ t }) => {
  const { filters, service } = usePurchaseMarket()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<DistributionData | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)

      try {
        // Determine location type and value from filters
        let locationType = undefined
        let locationValue = undefined

        if (filters.location) {
          switch (filters.locationType) {
            case 'postal_code':
              locationType = 'postal_code_name'
              break
            case 'city':
              locationType = 'municipality_name'
              break
            case 'district':
              locationType = 'district_name'
              break
          }
          locationValue = filters.location
        }

        // Calculate date range for last 12 months
        const endDate = new Date()
        const startDate = new Date()
        startDate.setMonth(startDate.getMonth() - 12)

        const apiFilters = {
          deal_type: 'purchase',
          property_category:
            filters.propertyCategory.length > 0 ? filters.propertyCategory : undefined,
          location: locationValue,
          location_type: locationType,
          active_listing_timestamp: {
            min: startDate.toISOString().split('T')[0],
            max: endDate.toISOString().split('T')[0],
          },
          outlier_field: 'price',
          outlier_percentage: 2,
        }

        const response = await service.getDistributionData(apiFilters)
        const processedData = processMarketDistributionData(response.aggregations, false)
        setData(processedData)
      } catch (err) {
        console.error('Error fetching distribution data:', err)
        setError(t('market.common.errors.loadingData'))
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [filters, service, t])

  return <DistributionSection data={data} loading={loading} error={error} isRental={false} t={t} />
}
